import i18next from 'i18next';
import languageDetector from 'i18next-browser-languagedetector';
import { useCallback, useMemo } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { missionsServer } from '../config';
import { logger } from '../log';
import { MissionsApi } from '../missions';
import { trackEvent } from '../tracking';
import { formatDuration } from '../utils';
import cz from './cz.json';
import de from './de.json';
import en from './en.json';
import es from './es.json';
import fr from './fr.json';
import gr from './gr.json';
import hr from './hr.json';
import hu from './hu.json';
import it from './it.json';
import lx from './lx.json';
import nl from './nl.json';
import pl from './pl.json';
import sv from './sv.json';

const log = logger('i18n');
const defaultLanguage = 'de';
const resources: Record<string, Record<string, Record<string, unknown>>> = { de, en, es, fr, gr, it, lx, pl, hu, sv, hr, cz, nl };

const languages = [
  { code: 'de', label: 'Deutsch (German)', complete: true },
  { code: 'en', label: 'English (English)', complete: true },
  { code: 'es', label: 'Español (Spanish)', complete: true },
  { code: 'fr', label: 'Français (French)', complete: true },
  { code: 'nl', label: 'Nederlands (Dutch)', complete: true },
  { code: 'it', label: 'Italiano (Italian)', complete: true },
  { code: 'pl', label: 'Polski (Polish)', complete: true },
  { code: 'cz', label: 'Čeština (Czech)', complete: true },
  { code: 'lx', label: 'Lëtzebuergesch (Luxembourgish)', complete: true },
  { code: 'hu', label: 'Magyar (Hungarian)', complete: true },
  { code: 'gr', label: 'Ελληνικά (Greek)', complete: true },
  { code: 'sv', label: 'Svenska (Swedish)', complete: true },
  { code: 'hr', label: 'Hrvatski (Croatian)', complete: true },
].sort((a, b) => a.label.localeCompare(b.label));

const knownMissingKeys = new Set<string>();

const supportedLngs = languages.map((l) => l.code);

const i18n = i18next.createInstance({
  ns: 'common',
  defaultNS: 'common',
  fallbackLng: defaultLanguage,
  fallbackNS: 'common',
  supportedLngs,
  resources,
  interpolation: {
    escapeValue: false,
    format: (value, format, lng) => {
      const t = i18n.t.bind(i18n);
      if (value instanceof Date) {
        if (format === 'date') return value.toLocaleDateString(lng);
        if (format === 'datetime') return value.toLocaleString(lng);
        if (format === 'duration') return formatDuration(value.getTime(), t);
        return value.toLocaleString(lng);
      }
      if (typeof value === 'number') {
        if (format === 'int') return value.toLocaleString(lng, { maximumFractionDigits: 0 });
        if (format === 'date') return new Date(value).toLocaleDateString(lng);
        if (format === 'duration') return formatDuration(value, t);
      }
      return value;
    },
  },
  saveMissing: import.meta.env.NODE_ENV === 'development',
  missingKeyHandler: (lng, ns, key) => {
    const cacheKey = `${ns}:${key}:${lng}`;
    if (knownMissingKeys.has(cacheKey)) return;
    knownMissingKeys.add(cacheKey);
    log.warn(`Missing translation in ${lng}: ${ns}/${key}`);
  },
});

i18n.use(languageDetector).use(initReactI18next);
i18n.on('languageChanged', (lang) => loadMissionApiResources(lang));
i18n.init();

async function loadMissionApiResources(lang: string) {
  log.info(`Loading mission-api translations in ${lang}...`);
  try {
    const bundle = await new MissionsApi({ BASE: missionsServer }).general.getTranslations(lang as any);
    log.info(`Loaded mission-api translations in ${lang}.`);
    i18n.addResourceBundle(lang, 'mission-api', bundle);
  } catch (e) {
    log.error(`Error loading mission-api translations in ${lang}: ${e}`);
  }
}

export function useLanguages() {
  const { i18n } = useTranslation();

  return useMemo(
    () =>
      languages.map((lang) => ({
        code: lang.code,
        label: lang.label,
        complete: lang.complete,
        active: lang.code === i18n.language,
      })),
    [i18n.language]
  );
}

export function useSetLanguage() {
  const { i18n } = useTranslation();

  return useCallback(
    (code: string) => {
      if (!languages.some((l) => l.code === code)) throw new Error(`Unknown language: ${code}`);
      trackEvent('Set language', { category: 'Settings', label: code });
      window.localStorage.setItem('i18nextLng', code);
      return i18n.changeLanguage(code);
    },
    [i18n]
  );
}

export function useCapitalize() {
  const {
    i18n: { language },
  } = useTranslation();
  return useCallback((s: string) => capitalize(s, language), [language]);
}

export function capitalize(s: string, locale?: string): string {
  if (!s.length) return s;
  if (s.length === 1) return s.toLocaleUpperCase(locale);
  return s[0].toLocaleUpperCase(locale) + s.slice(1);
}

export default i18n;
